/**
 * Which organisation or campaign the page on screen belongs to, and what the
 * caller may do there (platform design §17.8).
 *
 * The URL is the only source: `$campaignId` when the route has one, else
 * `$tenantId`, matched against the listing `/control/me` returned. No
 * component state holds a selection, so a reload, a bookmark and a pasted
 * link resolve identically.
 */

import { interpolatePath, useParams } from '@tanstack/react-router';

import type { LastUsed } from './lastUsed';
import { useMe, type CampaignEntry, type OrganisationEntry } from './session';

export const ORGANISATION_HOME_PATH = '/t/$tenantId';

export type OrgContext =
  | { kind: 'unanswered' }
  | { kind: 'none' }
  | { kind: 'not-yours'; scope: 'organisation' | 'campaign' }
  | {
      kind: 'organisation';
      organisation: OrganisationEntry;
      campaign: null;
      capabilities: readonly string[];
    }
  | {
      kind: 'campaign';
      organisation: OrganisationEntry;
      campaign: CampaignEntry;
      capabilities: readonly string[];
    };

export function resolveOrgContext(
  organisations: readonly OrganisationEntry[] | null,
  params: { tenantId: string | null; campaignId: string | null },
): OrgContext {
  if (organisations === null) return { kind: 'unanswered' };
  if (params.campaignId !== null) {
    for (const organisation of organisations) {
      const campaign = organisation.campaigns.find((c) => c.campaign_id === params.campaignId);
      if (campaign !== undefined) {
        return { kind: 'campaign', organisation, campaign, capabilities: campaign.capabilities };
      }
    }
    return { kind: 'not-yours', scope: 'campaign' };
  }
  if (params.tenantId !== null) {
    const organisation = organisations.find((o) => o.tenant_id === params.tenantId);
    return organisation === undefined
      ? { kind: 'not-yours', scope: 'organisation' }
      : {
          kind: 'organisation',
          organisation,
          campaign: null,
          capabilities: organisation.capabilities,
        };
  }
  return { kind: 'none' };
}

export function useOrgContext(): OrgContext {
  const me = useMe();
  // Untyped for the same reason as `control/routes.ts`'s `useRouteParams`:
  // the route tree is composed at runtime.
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  return resolveOrgContext(me.data?.organisations ?? null, {
    tenantId: params.tenantId ?? null,
    campaignId: params.campaignId ?? null,
  });
}

/** The organisation that owns the campaign on screen. `null` for every other
 *  context: a campaign route whose campaign is not in the listing renders
 *  nothing, and the API refuses its calls regardless. */
export function campaignOrganisationId(context: OrgContext): string | null {
  return context.kind === 'campaign' ? context.organisation.tenant_id : null;
}

export function useCampaignOrganisationId(): string | null {
  return campaignOrganisationId(useOrgContext());
}

/**
 * Deny by default. Answers from the organisation or campaign the route
 * names, never from a list pooled across organisations: write access in one
 * organisation says nothing about another (platform design §17.8).
 */
export function useCan(capability: string): boolean {
  const context = useOrgContext();
  return (
    (context.kind === 'organisation' || context.kind === 'campaign') &&
    context.capabilities.includes(capability)
  );
}

export function organisationHref(tenantId: string): string {
  return interpolatePath({ path: ORGANISATION_HOME_PATH, params: { tenantId } }).interpolatedPath;
}

/** Where a listed campaign opens. Named by the caller: the session layer does not know the data
 *  plane's routes, so `campaignHref` and `landingHref` both take it as an argument rather than
 *  each choosing their own. */
export type CampaignDestination = (campaignId: string) => string;

export function campaignHref(campaignId: string, campaignDestination: CampaignDestination): string {
  return campaignDestination(campaignId);
}

/** Home with a tenant-wide read grant, else the first listed campaign, else
 *  none. */
export function defaultHref(
  organisation: OrganisationEntry,
  campaignDestination: CampaignDestination,
): string | null {
  if (organisation.tenant_wide_read) return organisationHref(organisation.tenant_id);
  const first = organisation.campaigns[0];
  return first === undefined ? null : campaignDestination(first.campaign_id);
}

/** Where `/` sends a caller with memberships. `null` means no
 *  organisation in the listing opens any page for them. */
export function landingHref(
  organisations: readonly OrganisationEntry[],
  lastUsed: LastUsed | null,
  campaignDestination: CampaignDestination,
): string | null {
  const recorded = organisations.find((o) => o.tenant_id === lastUsed?.tenantId);
  if (recorded !== undefined && lastUsed !== null) {
    const campaignId = lastUsed.campaignId;
    if (campaignId !== null && recorded.campaigns.some((c) => c.campaign_id === campaignId)) {
      return campaignDestination(campaignId);
    }
    const fallback = defaultHref(recorded, campaignDestination);
    if (fallback !== null) return fallback;
  }
  for (const organisation of organisations) {
    const href = defaultHref(organisation, campaignDestination);
    if (href !== null) return href;
  }
  return null;
}
